 
import React from 'react';
import { UncontrolledTooltip } from 'reactstrap';
import {
    useReactTable,
    getCoreRowModel,
    getFilteredRowModel,
    ColumnDef,
    flexRender,
} from '@tanstack/react-table';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    NSTable,
    NSCellInput,
} from 'bricks';
import { faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import RenderIf from 'components/RenderIf/RenderIf';
import { UnitMix } from 'views/ProFormaTable/types';
import {
    OPERATIONS_INCOME_WS_HANDLERS, CASH_FLOW_TOOLTIP_MESSAGE,
    RSF_BUILT_GSF_DISCREPANCY_TOOLTIP_OPERATIONS_TEXT,
} from 'views/ProFormaTable/constants';
import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { doesRentableSFExceedBuiltGSF } from 'views/ProFormaTable/helpers';
import IconWithTooltip from 'components/IconWithTooltip/IconWithTooltip';
import { useProFormaSocketContext } from '../../../../socketContext/ProFormaSocketProvider';
import './UnitMixTable.scss';

export interface IUnitMixTableProps {
    operationBeginIndex: number;
    baseCashFlowColumns: ColumnDef<any>[];
}

const UnitMixTable = ({ operationBeginIndex, baseCashFlowColumns }: IUnitMixTableProps) => {
    const {
        incomeSummary, handleCreateOperationsRow, handleUpdateOperationsRow, handleDeleteOperationsRow,
        showCents, formatDisplayValue, operationsCashFlow, proForma, operatingInfo,
    } = useProFormaSocketContext();

    const handleUpdateUnitMix = (id: number, field: string) => handleUpdateOperationsRow(OPERATIONS_INCOME_WS_HANDLERS.UNIT_MIX.UPDATE, id, field);

    const shouldShowRSFWarning = doesRentableSFExceedBuiltGSF(
        proForma.proFormaModelId, Number(proForma.builtGrossSquareFootage), incomeSummary, operatingInfo.incomeCalculationType,
    );

    const unitMixCashFlowColumns: ColumnDef<UnitMix>[] = baseCashFlowColumns.map((column, cashFlowIndex) => ({
        ...column,
        id: `unit-mix-period-${column.id}`,
        accessorFn: (row, unitMixIndex: number) => (
            typeof operationsCashFlow?.[operationBeginIndex]?.units?.[unitMixIndex]?.value === 'number'
                ? (operationsCashFlow?.[cashFlowIndex]?.units?.[unitMixIndex]?.value || 0)
                : null
        ),
        footer: () => (
            <span className="text-dark">
                {operationBeginIndex
                    ? formatDisplayValue(operationsCashFlow?.[cashFlowIndex]?.grossRent || 0, showCents, 'currency')
                    : ' '}
            </span>
        ),
    }));

    const unitMixColumns: ColumnDef<UnitMix>[] = [
        {
            accessorKey: 'name',
            id: 'name',
            header: () => (
                <div className="d-flex align-items-center justify-content-between w-100 px-1">
                    <span>Unit mix</span>
                    <FontAwesomeIcon
                        id="plus-icon"
                        icon={faPlus}
                        className="ml-2 text-muted cursor--pointer"
                        onClick={() => handleCreateOperationsRow(OPERATIONS_INCOME_WS_HANDLERS.UNIT_MIX.CREATE)}
                    />
                </div>
            ),
            cell: ({ row, getValue }) => (
                <div className="d-flex align-items-center justify-content-between w-100 pr-1">
                    <div className="w-100">
                        <NSCellInput
                            type="text"
                            value={getValue()}
                            handleUpdateValue={updatedValue => {
                                const updateFn = handleUpdateUnitMix(Number(row.original.id), 'name');
                                if (typeof updateFn === 'function') {
                                    updateFn({
                                        target: { value: updatedValue },
                                    } as React.ChangeEvent<HTMLInputElement>);
                                }
                            }}
                            placeholder="Enter name"
                        />
                    </div>
                    <FontAwesomeIcon
                        id="trash-icon"
                        icon={faTrash}
                        className="ml-1 text-dark cursor--pointer"
                        onClick={() => handleDeleteOperationsRow(OPERATIONS_INCOME_WS_HANDLERS.UNIT_MIX.DELETE, Number(row.original.id))}
                    />
                </div>
            ),
            footer: () => <span className="px-1">Gross rent</span>,
        },
        {
            accessorKey: 'units',
            id: 'units',
            header: () => <span>Units</span>,
            cell: ({ row, getValue }) => (
                <NSCellInput
                    type="number"
                    value={getValue()}
                    handleUpdateValue={updatedValue => {
                        const updateFn = handleUpdateUnitMix(Number(row.original.id), 'units');
                        if (typeof updateFn === 'function') {
                            updateFn({
                                target: { value: updatedValue },
                            } as React.ChangeEvent<HTMLInputElement>);
                        }
                    }}
                    isRightAligned
                    placeholder="Enter value"
                />
            ),
            footer: () => <span>{formatDisplayValue(incomeSummary.grossRent.totalUnits || 0, false)}</span>,
        },
        {
            accessorKey: 'averageSf',
            id: 'averageSf',
            header: () => (
                <div className={classNames({ 'd-flex': shouldShowRSFWarning })}>
                    <RenderIf isTrue={shouldShowRSFWarning}>
                        <IconWithTooltip
                            name={`RSFDiscrepancyIcon-${proForma.proFormaModelId}`}
                            icon={faTriangleExclamation}
                            tooltipText={RSF_BUILT_GSF_DISCREPANCY_TOOLTIP_OPERATIONS_TEXT}
                            iconClassName="text-warning"
                            className="my-auto mx-1"
                        />
                    </RenderIf>
                    <span>Average RSF</span>
                </div>
            ),
            cell: ({ row, getValue }) => (
                <NSCellInput
                    type="number"
                    value={getValue()}
                    handleUpdateValue={updatedValue => {
                        const updateFn = handleUpdateUnitMix(Number(row.original.id), 'averageSf');
                        if (typeof updateFn === 'function') {
                            updateFn({
                                target: { value: updatedValue },
                            } as React.ChangeEvent<HTMLInputElement>);
                        }
                    }}
                    isRightAligned
                    placeholder="Enter value"
                />
            ),
            footer: () => <span>{formatDisplayValue(incomeSummary.grossRent.averageSf || 0, false)}</span>,
        },
        {
            accessorKey: 'pricePerSf',
            id: 'pricePerSf',
            header: () => <span>$/RSF/mo.</span>,
            cell: ({ row, getValue }) => (
                <NSCellInput
                    showCents
                    type="number"
                    numberType="currency"
                    value={getValue()}
                    handleUpdateValue={updatedValue => {
                        const updateFn = handleUpdateUnitMix(Number(row.original.id), 'pricePerSf');
                        if (typeof updateFn === 'function') {
                            updateFn({
                                target: { value: updatedValue },
                            } as React.ChangeEvent<HTMLInputElement>);
                        }
                    }}
                    isRightAligned
                    placeholder="Enter amount"
                />
            ),
            footer: () => <span>{formatDisplayValue(incomeSummary.grossRent.pricePerSf || 0, true, 'currency')}</span>,
        },
        {
            accessorKey: 'pricePerUnit',
            id: 'pricePerUnit',
            header: () => <span>$/unit/mo.</span>,
            cell: ({ row, getValue }) => (
                <NSCellInput
                    showCents
                    type="number"
                    numberType="currency"
                    value={getValue()}
                    handleUpdateValue={updatedValue => {
                        const updateFn = handleUpdateUnitMix(Number(row.original.id), 'pricePerUnit');
                        if (typeof updateFn === 'function') {
                            updateFn({
                                target: { value: updatedValue },
                            } as React.ChangeEvent<HTMLInputElement>);
                        }
                    }}
                    isRightAligned
                    placeholder="Enter amount"
                />
            ),
            footer: () => <span>{formatDisplayValue(incomeSummary.grossRent.pricePerUnit || 0, true, 'currency')}</span>,
        },
        {
            accessorKey: 'amountPerYear',
            id: 'amountPerYear',
            header: () => <span className="pr-1">Amount/year</span>,
            cell: ({ row, getValue }) => (
                <div className="pr-1">
                    <NSCellInput
                        showCents={showCents}
                        type="number"
                        numberType="currency"
                        value={getValue()}
                        handleUpdateValue={updatedValue => {
                            const updateFn = handleUpdateUnitMix(Number(row.original.id), 'amountPerYear');
                            if (typeof updateFn === 'function') {
                                updateFn({
                                    target: { value: updatedValue },
                                } as React.ChangeEvent<HTMLInputElement>);
                            }
                        }}
                        isRightAligned
                        placeholder="Enter amount"
                    />
                </div>
            ),
            footer: () => (
                <span className="pr-1">
                    {formatDisplayValue(incomeSummary.grossRent.totalAmountPerYear || 0, showCents, 'currency')}
                </span>
            ),
        },
        ...unitMixCashFlowColumns,
    ];

    const table = useReactTable({
        data: incomeSummary.unitMix || [],
        columns: unitMixColumns,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
    });

    return (
        <NSTable className="NSTable UnitMixTable mb-1">
            <thead className="NSTable__thead">
                {table.getHeaderGroups().map(headerGroup => (
                    <tr key={headerGroup.id} className="NSTable__thead__tr">
                        {headerGroup.headers.map(header => (
                            <th key={header.id} className="NSTable__thead__tr__th p-1">
                                {header.isPlaceholder ? null : (
                                    <div>
                                        {flexRender(
                                            header.column.columnDef.header,
                                            header.getContext(),
                                        )}
                                    </div>
                                )}
                            </th>
                        ))}
                    </tr>
                ))}
            </thead>
            <tbody
                className={classNames('NSTable__tbody', {
                    'NSTable__tbody--pro-forma-placeholder': !table.getRowModel().rows.length,
                })}
            >
                <RenderIf isTrue={table.getRowModel().rows.length}>
                    {table.getRowModel().rows.map(row => (
                        <tr key={row.id} className="NSTable__tbody__tr NSTable__tbody__tr--level-1">
                            {row.getVisibleCells().map(cell => (
                                <td key={cell.id} className="NSTable__tbody__tr__td p-1">
                                    {flexRender(
                                        cell.column.columnDef.cell,
                                        cell.getContext(),
                                    )}
                                </td>
                            ))}
                        </tr>
                    ))}
                </RenderIf>
                <RenderIf isTrue={!table.getRowModel().rows.length}>
                    <tr className="NSTable__tbody__tr NSTable__tbody__tr--level-1">
                        <td colSpan={6} className="NSTable__tbody__tr__td text-center text-muted py-4">
                            <div className="text-dark">Your added units will appear here.</div>
                            <div className="small">
                                Click the
                                {' '}
                                <span className="font-weight-bold text-dark">[+]</span>
                                {' '}
                                symbol in the
                                {' '}
                                <span className="font-weight-bold text-dark">Unit mix</span>
                                {' '}
                                header to add your first unit.
                            </div>
                        </td>
                        {baseCashFlowColumns.map((column, index) => (
                            <td
                                key={`unit-mix-period-${column.id}`}
                                className="NSTable__tbody__tr__td"
                                id={`unit-mix-placeholder-period-${index}`}
                            >
                                <RenderIf isTrue={baseCashFlowColumns.length === 1 && index === 0}>
                                    <UncontrolledTooltip target={`unit-mix-placeholder-period-${index}`}>
                                        {CASH_FLOW_TOOLTIP_MESSAGE}
                                    </UncontrolledTooltip>
                                </RenderIf>
                            </td>
                        ))}
                    </tr>
                </RenderIf>
            </tbody>
            <tfoot className="NSTable__tfoot">
                {table.getFooterGroups().map(footerGroup => (
                    <tr key={footerGroup.id} className="NSTable__tfoot__tr">
                        {footerGroup.headers.map(header => (
                            <th key={header.id} className="NSTable__tfoot__tr__th p-1">
                                {flexRender(
                                    header.column.columnDef.footer,
                                    header.getContext(),
                                )}
                            </th>
                        ))}
                    </tr>
                ))}
            </tfoot>
        </NSTable>
    );
};

export default UnitMixTable;
